/* eslint no-underscore-dangle:0 */
/* eslint-disable import/no-extraneous-dependencies */
import { applyMiddleware, createStore, compose as reduxCompose } from 'redux'
import thunk from 'redux-thunk'
import { createLogger } from 'redux-logger'
import reduxCookiesMiddleware, { getStateFromCookies } from 'redux-cookies-middleware'
import immutableStateInvariantMiddleware from 'redux-immutable-state-invariant'
import Cookies from 'js-cookie'
import {
  AUTOPLAY,
  DISMISSED_LOGIN_GATE,
  DISMISSED_EMBEDDED_DUTIES_MODAL,
  DISMISSED_FEEDBACK_CARD,
  VIEWED_INVITE_PAGE,
} from 'cookies.yml'
import reducer from 'base/reducers'
import eventsMiddleware from 'base/store/middleware/events'

// Dot-notation representation of the state attributes that we wish to persist
// to/from the browser cookies.
const statePathsForCookies = {
  'session.visitor.lastDismissedLoginGate': {
    name: DISMISSED_LOGIN_GATE,
    expiry: 365,
  },
  'session.visitor.lastDismissedEmbeddedDutiesModal': {
    name: DISMISSED_EMBEDDED_DUTIES_MODAL,
    expiry: 365,
  },
  'session.visitor.lastDismissedFeedbackCard': {
    name: DISMISSED_FEEDBACK_CARD,
    expiry: 365,
  },
  'session.visitor.viewedInvitePage': {
    name: VIEWED_INVITE_PAGE,
  },
}

function getInitialState() {
  const state = {
    appConfig: { ...window.E?.config },
    pathHistory: [{ name: window.location.pathname, hash: window.location.hash }],
    menus: { main: [] },
    session: {
      visitor: {
        autoplay: Cookies.get(AUTOPLAY) !== 'false',
        lastDismissedLoginGate: null,
        lastDismissedEmbeddedDutiesModal: null,
      },
    },
  }

  return getStateFromCookies(state, statePathsForCookies)
}

export function configureStore() {
  // https://chrome.google.com/webstore/detail/redux-devtools/lmhkpmbekcpmknklioeibfkpmmfibljd?hl=en
  const compose = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || reduxCompose

  const middlewares = [
    // Allow us to dispatch asynchronous actions (also known as thunks).
    thunk,

    // Allow us to use redux-immutable-state-invariant in development,
    // which will throw an error for any state mutations
    process.env.NODE_ENV !== 'production' ? immutableStateInvariantMiddleware() : null,

    // Allow us to pass along events to be published along with our actions
    eventsMiddleware,

    // Allows us to derive state from browser cookies
    reduxCookiesMiddleware(statePathsForCookies, {
      setCookie: (name, value, expiry, secure = false) => {
        const cookieOptions = {
          secure,
          path: '/',
        }

        // redux-cookies-middleware doesn't provide a means to set an expiration
        // on a cookie, but they suggest overriding the `setCookie` metohd to
        // accomplish this. This block just pulls in the set expiration from the
        // `statePathsForCookies` hash and uses its `expiry` key. If no `expiry`
        // is set, no `expires` value is passed to js-cookies, thus making it a
        // session cookie.
        if (typeof expiry === 'undefined') {
          const cookieConfig = Object.values(statePathsForCookies).find(
            cookie => cookie.name === name,
          )

          const configuredExpiry = cookieConfig && cookieConfig.expiry

          if (Number.isInteger(configuredExpiry)) {
            cookieOptions.expires = configuredExpiry
          }
        } else {
          cookieOptions.expires = expiry
        }

        Cookies.set(name, value, cookieOptions)
      },
    }),

    // Allow us to show actions in the console if we want (set this cookie to enable)
    // Note: Probably want to be using react devtools instead.
    Cookies.get('redux-logger') ? createLogger({ collapsed: true }) : null,
  ].filter(m => m) // Remove anything in the array that evaluated to false

  // Note: there is only ever one store instance. Subsequent calls to this fucntion will error.
  return createStore(
    // Pure function that returns new state when actions are dispatched
    reducer,

    // Bootstrapped data from the server used to kickstart the application
    getInitialState(),

    // Optional "store enhancer" - in this case we apply a few middlewares
    compose(applyMiddleware(...middlewares)),
  )
}

// There is only ever one store.
// Therefore, this should be the only place we call `configureStore`
export default configureStore()
